<script setup lang="ts">
import {
  TaskStatus,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationVariantAnswersSummary,
} from '@/api';
import { describeConstraints } from '@/utils/contractGeneration/questions';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

/**
 * The documents still consistent with what has been answered.
 *
 * Several is the normal state, not an error: matching narrows, it doesn't
 * resolve, and answering more questions is what gets to one. Each candidate is
 * labelled by what it stands for rather than by its name alone — with
 * questions in play, the name is bookkeeping and the answers are the meaning.
 */
interface Props {
  candidates: ContractGenerationVariantAnswersSummary[];
  questions: ContractGenerationTemplateQuestionInDb[];
  selectedUid?: string | null;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { selectedUid: null, disabled: false });

const emit = defineEmits<{
  (e: 'select', variantUid: string): void;
}>();

/** A document that failed validation or is still being checked can't be drafted. */
function isUsable(candidate: ContractGenerationVariantAnswersSummary): boolean {
  const status = candidate.template_status ?? null;
  return status === null || status === TaskStatus.DONE;
}

function statusNote(candidate: ContractGenerationVariantAnswersSummary): string | null {
  const status = candidate.template_status ?? null;
  if (status === TaskStatus.FAILED) return 'validation failed';
  if (status === TaskStatus.PENDING || status === TaskStatus.RUNNING) return 'still validating';
  return null;
}

const border = gaiaNeutral.borderCard;
const hover = gaiaNeutral.surfaceHover;
const surface = gaiaNeutral.surface;
const markBorder = gaiaNeutral.border;
const accent = gaiaAccent.base;
const accentTint = gaiaAccent.tint;
</script>

<template>
  <div class="candidate-list">
    <button
      v-for="candidate in props.candidates"
      :key="candidate.variant_uid"
      type="button"
      class="candidate"
      :class="{ selected: candidate.variant_uid === props.selectedUid }"
      :disabled="props.disabled || !isUsable(candidate)"
      @click="emit('select', candidate.variant_uid)"
    >
      <span class="candidate__mark" aria-hidden="true" />
      <n-flex vertical :size="5" :style="{ minWidth: 0, flex: 1 }">
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text size="dense" weight="semibold" tone="ink" truncate>
            {{ candidate.variant_name }}
          </gaia-text>
          <n-flex
            v-if="statusNote(candidate)"
            align="center"
            :size="5"
            :wrap="false"
            :style="{ marginLeft: 'auto' }"
          >
            <gaia-status-indicator status="alert" />
            <gaia-text size="micro" tone="alert">{{ statusNote(candidate) }}</gaia-text>
          </n-flex>
        </n-flex>
        <n-flex v-if="props.questions.length > 0" align="center" :size="5" :wrap="true">
          <gaia-tag
            v-for="(answer, index) in describeConstraints(candidate, props.questions)"
            :key="index"
            size="tiny"
            round
          >
            {{ answer.question }}: {{ answer.value }}
          </gaia-tag>
          <gaia-text
            v-if="describeConstraints(candidate, props.questions).length === 0"
            size="micro"
            tone="subtle"
          >
            Fits every answer
          </gaia-text>
        </n-flex>
      </n-flex>
    </button>
  </div>
</template>

<style scoped>
/* A list of pickable cards: naive has no control for a radio group whose
   options are multi-line cards, so the row chrome is local over tokens. */
.candidate-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.candidate {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease;
}

.candidate:hover:not(:disabled) {
  border-color: v-bind(accent);
  background: v-bind(hover);
}

.candidate:hover:not(:disabled) .candidate__mark {
  border-color: v-bind(accent);
}

.candidate.selected {
  border-color: v-bind(accent);
  background: v-bind(accentTint);
}

/* The affordance that makes a row of cards read as a choice rather than a
   summary. naive has no radio whose option is a multi-line card, so it is a
   mark of our own — filled from the centre when the row is the chosen one. */
.candidate__mark {
  flex: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid v-bind(markBorder);
  border-radius: 50%;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.candidate.selected .candidate__mark {
  border-color: v-bind(accent);
  box-shadow: inset 0 0 0 4px v-bind(accent);
}

.candidate:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
